/**
 * Labels and empty copy for data-request tiles.
 * The page stores `{ type, key }` — not report rows. Empty is empty.
 */

export const STAT_META = {
  users: {
    title: 'Users',
    empty:
      'Just you so far. Invite your leadership team and they show up here.',
  },
  contacts: {
    title: 'Contacts',
    empty:
      'No contacts yet. Import your team to give retention someone to retain.',
  },
  onboardees: {
    title: 'Onboardees',
    empty: 'Nobody is onboarding yet. Once Start Date is set, they count here.',
  },
  trainees: {
    title: 'In training',
    empty: 'Nobody is in training yet.',
  },
  lessonsCount: {
    title: 'Lessons',
    empty: 'No lessons yet. Your first one takes an afternoon, or a prompt.',
  },
  completion: {
    title: 'Average completion',
    empty: 'Completion shows up after your first lesson goes out.',
  },
  mal: {
    title: 'Monthly active learners',
    empty: 'Once lessons go out, we count who actually learns each month.',
  },
  certified: {
    title: 'Certified',
    empty:
      'Nobody is certified yet. A passing score can write this field for you.',
  },
  smsLine: {
    title: 'SMS line',
    empty: 'No SMS line yet. This is the single most important retention move.',
  },
  sla: {
    title: 'Response time',
    empty: 'Once conversations start, we track how fast your team answers.',
  },
  unanswered: {
    title: 'Waiting on you',
    empty: 'Inbox zero. Nothing is waiting on a reply.',
  },
  fieldAi: {
    title: 'Field AI answers',
    empty:
      'Upload a few files and Field AI starts answering rep questions from them.',
  },
  paperwork: {
    title: 'Paperwork pending',
    empty: 'Everyone hired has their paperwork in, or nobody is hired yet.',
  },
  day1: {
    title: 'Day 1 this week',
    empty: 'No start dates this week.',
  },
  daysToSale: {
    title: 'Days to first sale',
    empty: 'We measure this once the onboarding funnel reaches First sale.',
  },
  completed: {
    title: 'Completed onboarding',
    empty: 'Nobody has completed onboarding yet this month.',
  },
  candidates: {
    title: 'New candidates',
    empty:
      'No inbound candidates yet. A text to apply keyword is the fastest top of funnel.',
  },
  giScheduled: {
    title: 'Scheduled this week',
    empty: 'Nobody is scheduled. Reminders and a keyword fill this fast.',
  },
  giShowRate: {
    title: 'Show rate',
    empty: 'Show rate appears after your first group interview runs.',
  },
  noShows: {
    title: 'No-shows to recover',
    empty: 'No no-shows this week.',
  },
  oneOnOne: {
    title: 'Interviews today',
    empty: 'No one on one interviews on the calendar today.',
  },
  offers: {
    title: 'Offers out',
    empty: 'No offers waiting on an answer.',
  },
  orientation: {
    title: 'Next orientation',
    empty: 'No orientation on the calendar. Reminders create the seat count.',
  },
  hired: {
    title: 'Hired this month',
    empty: 'No hires yet this month.',
  },
};

export const REQUEST_META = {
  stories: {
    title: 'Stories',
    empty: 'No customer stories on this page yet.',
  },
  stat: {
    title: 'Stat',
    empty: 'Nothing here yet. This tile fills when the report has a value.',
  },
  grain: {
    title: 'Calls with your CSM',
    empty: 'Grain calls are not connected yet.',
  },
  funnel: {
    title: 'Funnel',
    empty: 'No funnel yet. Each section gets one as you set up its fields.',
  },
  health: {
    title: 'Account health',
    empty: 'Account health is not connected yet.',
  },
  consumption: {
    title: 'Portal search',
    empty:
      'Nobody has searched the portal yet. Invite people in and this fills with what they ask.',
  },
  banners: {
    title: 'Campaigns',
    empty: 'No campaigns or workflows on this page yet.',
  },
  table: {
    title: 'Table',
    empty: 'No rows yet. Custom objects land here once they exist.',
  },
  lesson: {
    title: 'Lesson',
    empty: 'No lesson linked to this block yet.',
  },
  tools: {
    title: 'Get people in',
    empty: 'On-page tools are listed here. They are not Cy prompts.',
  },
};

const PHASE_LABEL = {
  retain: 'Retain',
  train: 'Train',
  onboard: 'Onboard',
  recruit: 'Recruit',
  foundation: 'Foundation',
};

export function describeRequestTile(props = {}) {
  const type = String(props.requestType || 'stat');
  const key = String(props.requestKey || '').trim();
  const phase = String(props.phase || '').trim();
  const typeMeta = REQUEST_META[type] || REQUEST_META.stat;
  const statMeta = type === 'stat' ? STAT_META[key] : null;
  const bannerKind =
    type === 'banners' && key === 'workflows' ? 'Workflows' : '';
  const funnelTitle =
    type === 'funnel' && PHASE_LABEL[phase || key]
      ? `${PHASE_LABEL[phase || key]} funnel`
      : '';

  const title =
    props.title && props.title !== typeMeta.title && props.title !== type
      ? props.title
      : funnelTitle ||
        bannerKind ||
        (statMeta && statMeta.title) ||
        (type === 'table' && key ? key : '') ||
        typeMeta.title;

  const empty =
    (statMeta && statMeta.empty) ||
    (type === 'funnel'
      ? `No ${
          PHASE_LABEL[phase || key] || ''
        } funnel yet. It appears when the fields exist.`
          .replace(/\s+/g, ' ')
          .trim()
      : typeMeta.empty);

  return {
    type,
    key,
    phase,
    title,
    empty,
    minutes: props.minutes || null,
    items: props.items || [],
    itemCount: props.itemCount || null,
  };
}
